import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ApolloError } from '@apollo/client';
import { useGetEventByNameForPeoplePickerQuery } from './usePeoplePickerButtonController.graphql.generated';
import type { PersonFragment } from '@graphql/fragments/person.graphql.generated';
import type { LabelIdsToPeopleIdsMap, LabelViewItem, PersonViewItem, PersonItemRender } from './PeoplePicker';
import { loadable } from '@shared/core/loadableFromNamedExport';
import { useOverlayUnstable } from '@withjoy/joykit';
import { getInGroupPosition } from '@shared/utils/groupedListUtils';
import { isEqual } from 'lodash-es';
import { useTranslation } from '@shared/core';
import type { DeliveryMethodValue, OutsideScopeCopy, PeoplePickerUseCase, PickerLabel } from './PeoplePicker.types';
import { usePeopleContext } from '@shared/context/people';
import { hasValidPhone } from '@shared/utils/messaging/deliveryChannel';
import { getPersonName, getAvatarInitials } from '@shared/components/PeopleListItem/PeopleListItem.utils';
import { getAvatarUrl } from '@shared/utils/getAvatarUrl';
import { persistPreference } from '@shared/utils/messaging/deliveryChannel';
import { isPersonDisabled, getSublabelText, scopeUserLabels } from './PeoplePicker.utils';

const PeoplePickerDialog = loadable(() => import('./PeoplePicker.dialog'));

export type SelectedPerson = Readonly<Partial<PersonFragment & { full_name: string }>>;

export type HardcodedLabel = Readonly<{
  id: string;
  display: string;
  people: ReadonlyArray<{ id: string; personIdTag: string }>;
  initiallySelected?: boolean;
  labelDescription?: string;
  /**
   * Why this label can't be unchecked, shown on hover/focus. Only reaches the UI in
   * `requiredScope` mode, where the label is locked on. Supplied by the calling app so this
   * shared component doesn't own product copy.
   */
  lockedReason?: string;
  /** Optional action beside `labelDescription`, e.g. a retry when the label's people can't load. */
  labelAction?: Readonly<{ label: string; onClick: () => void }>;
}>;

/**
 * How `hardcodedLabels` combine with the event's own tags.
 *
 * - `exclusive` (default) — hardcoded labels replace the user's tags entirely. Legacy behavior,
 *   and the reason user tags were invisible in RSVP Reminder (PRNT-4488).
 * - `requiredScope` — hardcoded labels become a required, non-togglable scope, and the user's tags
 *   are listed alongside them and narrow *within* that scope: `scope AND (tag OR tag …)` (MSG-216).
 */
export type HardcodedLabelsMode = 'exclusive' | 'requiredScope';

export type StoredSelectedPerson = SelectedPerson &
  Readonly<{
    hasEmailDeliveryMethodSelected?: boolean;
    hasTextDeliveryMethodSelected?: boolean;
    hasPhone?: boolean;
    personName?: string;
    avatarInitials?: string;
    avatarUrl?: string;
  }>;

type UsePeoplePickerButtonControllerProps = Readonly<{
  title: React.ReactNode;
  eventHandle: string;
  onSelectedPeople?: (people: ReadonlyArray<StoredSelectedPerson>) => void;
  enableSmsSelection?: boolean;
  smsOnly?: boolean;
  emailOnly?: boolean;
  initialDeliveryMethod?: DeliveryMethodValue;
  /** When provided, persists the "if both are available, send via:" preference to sessionStorage. */
  preferenceStorageKey?: string;
  /** When true, people missing contact info for the delivery method can still be selected/deselected. */
  allowUnreachableSelection?: boolean;
  /** When true, renders the delivery method segmented controller inside the people picker dialog. */
  showDeliveryMethodSelector?: boolean;
  hardcodedLabels?: ReadonlyArray<HardcodedLabel>;
  /** Defaults to `exclusive` — see {@link HardcodedLabelsMode}. Ignored without `hardcodedLabels`. */
  hardcodedLabelsMode?: HardcodedLabelsMode;
  /** Wording for the outside-scope warning. Only reachable in `requiredScope` mode. */
  outsideScopeCopy?: OutsideScopeCopy;
  /** Copy for the guest list's select-all control in `requiredScope` mode. See PeoplePicker. */
  selectAllLabel?: string;
  personItemRender?: PersonItemRender;
  useCase: PeoplePickerUseCase;
  headerBanner?: React.ReactNode;
}>;

type UsePeoplePickerButtonControllerReturn = Readonly<{
  loading: boolean;
  error: ApolloError | undefined;
  selectedPeople: ReadonlyArray<StoredSelectedPerson>;
  startSelection: () => void;
  viewPeople: ReadonlyArray<PersonViewItem>;
  viewLabels: ReadonlyArray<LabelViewItem>;
}>;

export const usePeoplePickerButtonController = ({
  eventHandle,
  enableSmsSelection = false,
  smsOnly = false,
  emailOnly = false,
  initialDeliveryMethod,
  preferenceStorageKey,
  allowUnreachableSelection = false,
  showDeliveryMethodSelector = false,
  title,
  onSelectedPeople,
  hardcodedLabels,
  hardcodedLabelsMode = 'exclusive',
  outsideScopeCopy,
  selectAllLabel,
  personItemRender,
  useCase,
  headerBanner,
}: UsePeoplePickerButtonControllerProps): UsePeoplePickerButtonControllerReturn => {
  const { tq } = useTranslation('peoplePickerButton');
  const isRequiredScopeMode = !!hardcodedLabels && hardcodedLabelsMode === 'requiredScope';

  const { selectedPeople, setSelectedPeople } = usePeopleContext();
  const [viewLabels, setViewLabels] = useState<ReadonlyArray<LabelViewItem>>([]);
  const [viewPeople, setViewPeople] = useState<ReadonlyArray<PersonViewItem>>([]);
  const [labelIdsToPeopleIdsMap, setLabelIdsToPeopleIdsMap] = useState<LabelIdsToPeopleIdsMap>({});
  const [hasStartedSelection, setHasStartedSelection] = useState(false);

  // --- Refs to decouple delivery-method / delivery-flag changes from viewPeople recomputation ---
  // The providers already recompute delivery flags on selectedPeople via recomputeDeliveryFlags;
  // the dialog re-derives disabled states via REINITIALIZE_STATE on open.
  // Using refs here prevents the cascade: deliveryMethod change → viewPeople change →
  // setSelectedPeople → viewPeople change → overlay re-registration loop.
  const initialDeliveryMethodRef = useRef(initialDeliveryMethod);
  initialDeliveryMethodRef.current = initialDeliveryMethod;

  const selectedPeopleRef = useRef(selectedPeople);
  selectedPeopleRef.current = selectedPeople;

  // Stable fingerprint that changes only when the set of selected person IDs changes,
  // NOT when delivery flags are recomputed on existing selections.
  const selectedIdsKey = useMemo(
    () =>
      selectedPeople
        .map(p => p.id ?? p.personIdTag ?? '')
        .sort()
        .join(','),
    [selectedPeople],
  );

  const { data, error, loading } = useGetEventByNameForPeoplePickerQuery({
    batchMode: undefined,
    variables: { eventHandle },
  });

  // only api labels are filtered out if they have no people
  const apiLabels = useMemo<ReadonlyArray<PickerLabel>>(() => {
    const userLabels = data?.eventByName?.labels ?? [];
    const nonEmptyUserLabels = userLabels.filter(label => label.people && label.people.length > 0);

    if (!hardcodedLabels) return nonEmptyUserLabels;
    // Legacy `exclusive` mode: hardcoded labels stand in for the tag list entirely.
    if (!isRequiredScopeMode) return hardcodedLabels;

    return scopeUserLabels(hardcodedLabels, nonEmptyUserLabels);
  }, [data, hardcodedLabels, isRequiredScopeMode]);

  // Asked of the merged labels rather than the prop, because `scopeUserLabels` declines to apply an
  // empty scope — so requesting the mode is not the same as being in it.
  const isScopeActive = useMemo(() => apiLabels.some(label => label.isRequiredScope), [apiLabels]);

  const apiHouseholds = useMemo(() => {
    return data?.eventByName?.guests.households || [];
  }, [data]);

  const isPersonSelected = useCallback(
    (person: PersonFragment): boolean => {
      return selectedPeopleRef.current.some(selectedPerson => person.id === selectedPerson.id || person.personIdTag === selectedPerson.personIdTag);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reads from ref; selectedIdsKey ensures re-creation when selection membership changes
    [selectedIdsKey],
  );

  const getMatchingStoredSelectedPerson = useCallback(
    (person: PersonFragment): StoredSelectedPerson | undefined => {
      return selectedPeopleRef.current.find(selectedPerson => person.id === selectedPerson.id || person.personIdTag === selectedPerson.personIdTag);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reads from ref; selectedIdsKey ensures re-creation when selection membership changes
    [selectedIdsKey],
  );

  useEffect(() => {
    const newLabelIdsToPeopleIdsMap: Record<string, Array<string>> = {};
    const newViewLabels = apiLabels.map((label): LabelViewItem => {
      newLabelIdsToPeopleIdsMap[label.id] = label.people.map(person => person.id);
      const isRequiredScope = !!label.isRequiredScope;
      // A required scope is always on and can't be unchecked. Its sibling tags start unchecked so
      // the picker opens on the full, unnarrowed scope — deriving their checked state from the
      // current selection (the `exclusive`-mode rule below) would instead open with every tag that
      // happens to be a subset of the scope already checked, which reads as "narrowed by all tags".
      const selected = isRequiredScope
        ? true
        : !isScopeActive &&
          label.people.length > 0 &&
          label.people.every(person => selectedPeopleRef.current.some(sp => sp.id === person.id || sp.personIdTag === person.personIdTag));
      return {
        id: label.id,
        selected,
        notSelectable: isRequiredScope,
        isRequiredScope,
        labelText: label.display,
        count: label.people.length,
        disabled: label.people.length === 0,
        labelDescription: label.labelDescription,
        labelAction: label.labelAction,
        notSelectableReason: isRequiredScope ? label.lockedReason : undefined,
      };
    });

    const newViewPeople = apiHouseholds.reduce((acc, household) => {
      const leaderFirstName = household.members[0]?.firstName;
      const householdPersons = household.members.map((person, index): PersonViewItem => {
        // Trust BE-canonical `phoneE164` (only set when valid) before re-validating the
        // raw `phoneDisplay`, which the schema explicitly notes is "not necessarily valid".
        const hasValidPhoneNumber = !!person.phoneE164 || hasValidPhone(person.phoneDisplay);

        const isDisabled = isPersonDisabled({
          hasEmail: !!person.hasEmail,
          hasPhone: hasValidPhoneNumber,
          emailOnly,
          smsOnly,
          allowUnreachableSelection,
          enableSmsSelection,
          deliveryMethod: initialDeliveryMethodRef.current,
        });
        const storedSelectedPerson = getMatchingStoredSelectedPerson(person);

        const selected = isPersonSelected(person);
        const fullName = [person.firstName, person.lastName].filter(name => !!name).join(' ');
        const labelText = fullName || (index > 0 && leaderFirstName ? tq`${leaderFirstName}'s Guest` : '');
        return {
          id: person.id,
          personIdTag: person.personIdTag || undefined,
          selected,
          labelText,
          firstName: person.firstName ?? undefined,
          lastName: person.lastName ?? undefined,
          sublabelText: getSublabelText({ smsOnly, emailOnly, hasPhone: hasValidPhoneNumber, enableSmsSelection, hasEmail: !!person.hasEmail, tq }),
          disabled: isDisabled,
          groupId: person.household!.id,
          avatarName: [person.firstName, person.lastName].filter(name => !!name).join(' '),
          avatarUrl: getAvatarUrl(person),
          inGroupPosition: getInGroupPosition(index, household.members.length),
          email: person.email || '',
          phoneDisplay: person.phoneDisplay ?? undefined,
          hasPhoneNumber: hasValidPhoneNumber,
          hasEmailDeliveryMethodSelected: storedSelectedPerson?.hasEmailDeliveryMethodSelected ?? (selected && person.hasEmail && initialDeliveryMethodRef.current !== 'text'),
          hasTextDeliveryMethodSelected:
            storedSelectedPerson?.hasTextDeliveryMethodSelected ?? (enableSmsSelection && selected && hasValidPhoneNumber && initialDeliveryMethodRef.current !== 'email'),
        };
      });
      return [...acc, ...householdPersons];
    }, [] as ReadonlyArray<PersonViewItem>);

    if (!isEqual(newViewLabels, viewLabels)) {
      setViewLabels(newViewLabels);
    }
    if (!isEqual(newViewPeople, viewPeople)) {
      setViewPeople(newViewPeople);
    }
    if (!isEqual(newLabelIdsToPeopleIdsMap, labelIdsToPeopleIdsMap)) {
      setLabelIdsToPeopleIdsMap(newLabelIdsToPeopleIdsMap);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedPeople read via ref; selectedIdsKey triggers when selection membership changes
  }, [
    enableSmsSelection,
    apiLabels,
    apiHouseholds,
    isPersonSelected,
    selectedIdsKey,
    setViewLabels,
    setViewPeople,
    viewLabels,
    viewPeople,
    labelIdsToPeopleIdsMap,
    getMatchingStoredSelectedPerson,
    tq,
    smsOnly,
    emailOnly,
    allowUnreachableSelection,
    isScopeActive,
  ]);

  // Enrich the *existing* selection in place — fill in ids/names/avatars/contact data from the
  // loaded picker view — WITHOUT changing membership. Membership is owned by the initial selection
  // (e.g. useApplyInitiallySelectedPeople) and the picker dialog's `submit`. Deriving membership
  // here from `viewPeople.filter(selected)` set up a phase-lagged mutual recursion with the
  // viewPeople recompute below (selectedPeople ⇄ viewPeople.selected): when the initial selection
  // and the first computed viewPeople started out of sync, the two effects swapped values forever
  // (selectedPeople flapping between the two sets) and pinned the page in an infinite render loop.
  // Mapping over `prev` keeps this effect idempotent on membership, which breaks the cycle.
  // Uses a functional updater so delivery flags set by a provider's setDeliveryMethod (which may
  // have landed between the viewPeople recompute and this effect) are preserved.
  useEffect(() => {
    if (!viewPeople?.length) return;

    // Index the loaded view items by both id and personIdTag so a sparse selection (which may carry
    // only one of the two) can still find its enrichment source.
    const viewByKey = new Map<string, PersonViewItem>();
    for (const view of viewPeople) {
      if (view.id) viewByKey.set(view.id, view);
      if (view.personIdTag) viewByKey.set(view.personIdTag, view);
    }

    setSelectedPeople(prev => {
      const merged = prev.map(person => {
        const view = (person.id ? viewByKey.get(person.id) : undefined) ?? (person.personIdTag ? viewByKey.get(person.personIdTag) : undefined);
        // Not present in the loaded households (still loading, or a guest the picker query doesn't
        // return) → keep the existing entry untouched rather than dropping it.
        if (!view) return person;
        return {
          id: view.id,
          personIdTag: view.personIdTag,
          full_name: view.labelText,
          email: view.email,
          phoneDisplay: view.phoneDisplay,
          // Carry the picker's authoritative phone-validity decision through to persisted
          // selections so `recomputeDeliveryFlags` doesn't re-run libphonenumber on
          // `phoneDisplay` and undo the BE-canonical `phoneE164` trust.
          hasPhone: view.hasPhoneNumber,
          firstName: view.firstName,
          lastName: view.lastName,
          personName: getPersonName(view),
          avatarInitials: getAvatarInitials(view),
          avatarUrl: view.avatarUrl,
          // Preserve delivery flags from prev when defined — a provider's setDeliveryMethod may have
          // updated them after viewPeople was computed, so prev is more authoritative. Fall through
          // to the view-derived values when prev flags are undefined (sparse initial selection).
          hasEmailDeliveryMethodSelected: person.hasEmailDeliveryMethodSelected ?? view.hasEmailDeliveryMethodSelected,
          hasTextDeliveryMethodSelected: person.hasTextDeliveryMethodSelected ?? view.hasTextDeliveryMethodSelected,
        };
      });
      return isEqual(prev, merged) ? prev : merged;
    });
  }, [viewPeople, setSelectedPeople]);

  const submit = ({ mergedSelectionPeople, preference }: { mergedSelectionPeople: ReadonlyArray<PersonViewItem>; preference?: DeliveryMethodValue }) => {
    if (preferenceStorageKey && preference) {
      persistPreference(preferenceStorageKey, preference);
    }
    const newSelectedPeople = mergedSelectionPeople
      .filter(person => person.selected)
      .map(person => ({
        id: person.id,
        personIdTag: person.personIdTag,
        full_name: person.labelText,
        email: person.email,
        phoneDisplay: person.phoneDisplay,
        // Carry the picker's authoritative phone-validity decision through to persisted
        // selections so `recomputeDeliveryFlags` doesn't re-run libphonenumber on
        // `phoneDisplay` and undo the BE-canonical `phoneE164` trust.
        hasPhone: person.hasPhoneNumber,
        firstName: person.firstName,
        lastName: person.lastName,
        personName: getPersonName(person),
        avatarInitials: getAvatarInitials(person),
        avatarUrl: person.avatarUrl,

        // Collect the following two fields to determine what method we should use to contact the person
        hasEmailDeliveryMethodSelected: person.hasEmailDeliveryMethodSelected,
        hasTextDeliveryMethodSelected: person.hasTextDeliveryMethodSelected,
      }));
    if (onSelectedPeople) {
      onSelectedPeople(newSelectedPeople);
    }
    // it is used only specifically for the invitations/save the dates case, as no labels selection should be saved - only people
    setSelectedPeople(newSelectedPeople);
    setHasStartedSelection(false);
    // this should be the last step, since unmounting the component create a race condition and stale data of selected people
    hidePeoplePicker();
  };

  const cancel = () => {
    setHasStartedSelection(false);
    hidePeoplePicker();
  };

  const [showPeoplePicker, hidePeoplePicker] = useOverlayUnstable(
    {
      overlayKey: 'people_picker',
      render: overlayProps => {
        if (hasStartedSelection) {
          return (
            <PeoplePickerDialog
              enableSmsSelection={enableSmsSelection}
              smsOnly={smsOnly}
              emailOnly={emailOnly}
              initialDeliveryMethod={initialDeliveryMethod}
              showDeliveryMethodSelector={showDeliveryMethodSelector}
              // In `requiredScope` mode the list holds the user's actual tags alongside the scope,
              // so "Tags" is accurate again; only the legacy scope-less `exclusive` list is "Filters".
              tagsTitle={hardcodedLabels && !isRequiredScopeMode ? tq`Filters` : tq`Tags`}
              personItemRender={personItemRender}
              headerBanner={headerBanner}
              outsideScopeCopy={outsideScopeCopy}
              selectAllLabel={selectAllLabel}
              {...overlayProps}
            />
          );
        }

        return <></>;
      },
      props: {
        title,
        eventHandle,
        allPeople: viewPeople,
        allLabels: viewLabels,
        labelIdsToPeopleIdsMap,
        submit,
        cancel,
        useCase,
        hasStartedSelection,
      },
    },
    { dependencies: [viewPeople, viewLabels, hasStartedSelection, initialDeliveryMethod] },
  );

  const startSelection = useCallback(() => {
    setHasStartedSelection(true);
    showPeoplePicker();
  }, [showPeoplePicker]);

  return {
    loading,
    error,
    selectedPeople,
    startSelection,
    viewPeople,
    viewLabels,
  };
};
